/* SpawnAnything — placed buildings -> plain, JSON-safe simulation data for the worker's `setup` message.

   A placement is { key, team, level, x, z, rot }: `key` names a recipe (preset key or a generated one), x/z = plot centre in
   world metres, rot = number of 90° turns (0..3, yaw = rot * PI/2, three.js rotation.y convention: local +z -> (sin yaw, cos yaw)).
   Rotations use exact swaps instead of sin/cos so every client derives bit-identical data from the same placements.

   One entry per placement (index = building id in the sim):
   { id, key, team, level, x, z, rot, yaw, hw, hd,             hw/hd = world half extents of the full plot
     lv: [L1|null, L2|null, L3|null] }                          data for the placed level and every level above (for upgrades)
   level data: { hp, armor, h, cap, trains, spawn: [x, z],
     rects: [[x0, z0, x1, z1, pass], ...]                       world AABB colliders; pass = 1 -> owner team walks through (gates)
     guns: [{ x, y, z, yaw, range, minRange, dmg, cd, aoe, proj, pspeed, arc, hitscan, acc, knock,   sim
              tk, bx, by, bz, byaw }] }                         render: turret model key ('' = built-in weapon), base pos/yaw
   Gun order: the building's own weapon (stats.turret) first, then every hardpoint slot in compile order with its default turret. */
import { compileBuilding, SLOT_TURRETS, TURRETS, MAX_LEVEL } from './buildings.js';
import { BASE_CAP, RESERVE_MAX } from '../sim/buildingRules.js';

const cache = new Map();
/** compileBuilding with a cache per (recipe key, level). Recipes are immutable once registered under a key. */
export function compiledFor(key, recipe, level) {
  const id = key + ':' + level;
  let c = cache.get(id);
  if (!c) { c = compileBuilding(recipe, level); cache.set(id, c); }
  return c;
}
export function forgetCompiled(key) { for (let L = 1; L <= MAX_LEVEL; L++) cache.delete(key + ':' + L); }

/** rotate a local (x, z) offset by rot * 90° (exact) */
export function rot90(rot, x, z) {
  switch (rot & 3) {
    case 1: return [z, -x];
    case 2: return [-x, -z];
    case 3: return [-z, x];
    default: return [x, z];
  }
}
const r4 = v => Math.round(v * 1e4) / 1e4;

const TUR_INFO = {};
function turretInfo(tk) {
  if (!TUR_INFO[tk]) {
    const m = TURRETS[tk].model();
    TUR_INFO[tk] = { muzzleY: m.muzzle[1] * m.vs, pivotY: m.parts[m.headPart].pivot[1] * m.vs };
  }
  return TUR_INFO[tk];
}
const KNOCK = s => (s.aoe ? 8 : s.hitscan ? 0.6 : 2.5);

function levelData(c, p) {
  const st = c.stats, rot = p.rot & 3, byaw = rot * Math.PI / 2;
  const W = (lx, lz) => { const [rx, rz] = rot90(rot, lx, lz); return [r4(p.x + rx), r4(p.z + rz)]; };
  let cols = c.colliders;
  if (!cols.length) cols = [{ x: 0, z: 0, w: c.footprint.w * 0.5, d: c.footprint.d * 0.5, passTeam: false }];
  const rects = cols.map(r => {
    const [cx, cz] = W(r.x, r.z), odd = rot & 1, hw = (odd ? r.d : r.w) / 2, hd = (odd ? r.w : r.d) / 2;
    return [r4(cx - hw), r4(cz - hd), r4(cx + hw), r4(cz + hd), r.passTeam ? 1 : 0];
  });
  const guns = [];
  const gun = (s, lx, ly, lz, yaw, tk, base) => {
    const [x, z] = W(lx, lz), [bx, bz] = base ? W(base[0], base[2]) : [x, z];
    guns.push({
      x, y: r4(ly), z, yaw: r4(yaw), range: s.range, minRange: s.minRange || 0, dmg: s.dmg, cd: s.cd, aoe: s.aoe || 0,
      proj: s.hitscan ? 0 : Math.max(0, s.proj || 0), pspeed: s.pspeed || 0, arc: !!s.arc, hitscan: !!s.hitscan,
      acc: s.hitscan ? 0.85 : 1, knock: KNOCK(s), tk, bx, by: base ? r4(base[1]) : r4(ly), bz, byaw: r4(yaw),
    });
  };
  if (st.turret) { const f = st.turret.from; gun(st.turret, f[0], f[1], f[2], byaw, '', null); }
  for (const s of c.slots) {
    const tk = (SLOT_TURRETS[s.kind] || ['mg'])[0], ti = turretInfo(tk);
    gun(TURRETS[tk].stats, s.pos[0], s.pos[1] + ti.muzzleY, s.pos[2], byaw + s.yaw, tk, s.pos);
  }
  return { hp: st.hp, armor: st.armor, h: c.height, cap: st.troopCap || 0, trains: st.trains.length, spawn: W(c.spawn[0], c.spawn[1]), rects, guns };
}

/**
 * @param {{key:string, team:0|1, level:number, x:number, z:number, rot:number}[]} placed
 * @param {(key: string) => object} recipeOf  recipe for a key
 */
export function buildingSimData(placed, recipeOf) {
  return placed.map((p, id) => {
    const level = Math.min(MAX_LEVEL, Math.max(1, p.level | 0)), rec = recipeOf(p.key);
    const lv = [null, null, null];
    for (let L = level; L <= MAX_LEVEL; L++) lv[L - 1] = levelData(compiledFor(p.key, rec, L), p);
    const fp = compiledFor(p.key, rec, level).footprint, odd = p.rot & 1;
    return { id, key: p.key, team: p.team ? 1 : 0, level, x: p.x, z: p.z, rot: p.rot & 3, yaw: r4((p.rot & 3) * Math.PI / 2),
      hw: (odd ? fp.d : fp.w) / 2, hd: (odd ? fp.w : fp.d) / 2, lv };
  });
}

/** reserved unit slots per team for spawning (0 when the team has no building that trains units) */
export function spawnReserve(sim, team) {
  let trainer = false, cap = BASE_CAP;
  for (const b of sim) {
    if (b.team !== team) continue;
    const top = b.lv[MAX_LEVEL - 1];
    cap += top.cap;
    if (b.lv.some(l => l && l.trains)) trainer = true;
  }
  return trainer ? Math.min(RESERVE_MAX, cap) : 0;
}
